import { motion } from "framer-motion";
import { MapPin, Calendar, Clock } from "lucide-react";
import { Button } from "./ui/button";

// Optimized venue image
const eventDetails = {
  date: "Sunday, January 26th, 2025",
  time: "10:00 - 14:00 WIB",
  venue: "Hotel Mulia Senayan",
  address: "Jl. Asia Afrika Pintu IX, Gelora",
  city: "Jakarta Pusat, 10270",
  mapUrl: "https://maps.app.goo.gl/2B5uonaxqsrmMLfP8",
  image:
    "https://cdn.prod.website-files.com/6624ff6a5db57a668993dd5e/669f61646edc27c254ccc74d_IMG_0615.webp",
};

const LocationCard = () => {
  return (
    <section
      className="section-wrapper py-16 sm:py-20 md:py-32 px-4 sm:px-6"
      data-testid="location-section"
    >
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12"
        >
          <p className="font-serif-accent text-[10px] sm:text-xs tracking-[0.2em] sm:tracking-[0.3em] text-[#D4AF37] uppercase mb-3 sm:mb-4">
            Mark Your Calendar
          </p>
          <h2 className="font-serif-heading text-2xl sm:text-3xl md:text-5xl text-[#8B0000]">
            Date & Place
          </h2>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative"
        >
          {/* Decorative Frame */}
          <div className="absolute -inset-2 sm:-inset-3 md:-inset-4 border border-[#D4AF37] opacity-30 pointer-events-none" />

          <div className="bg-white/80 backdrop-blur-sm border border-[#D4AF37]/20 overflow-hidden">
            <div className="grid md:grid-cols-2">
              {/* Image */}
              <div className="relative h-48 sm:h-64 md:h-auto overflow-hidden">
                <img
                  src={eventDetails.image}
                  alt={eventDetails.venue}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  data-testid="venue-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent md:bg-gradient-to-r" />
              </div>

              {/* Details */}
              <div className="p-5 sm:p-8 md:p-12 flex flex-col justify-center">
                {/* Date */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-[#D4AF37] flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#8B0000]" />
                  </div>
                  <div>
                    <p className="font-serif-accent text-[10px] sm:text-xs tracking-wider text-[#D4AF37] uppercase mb-0.5 sm:mb-1">
                      Date
                    </p>
                    <p
                      className="font-serif-heading text-base sm:text-lg md:text-xl text-[#2D2D2D]"
                      data-testid="event-date"
                    >
                      {eventDetails.date}
                    </p>
                  </div>
                </motion.div>

                {/* Time */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-[#D4AF37] flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#8B0000]" />
                  </div>
                  <div>
                    <p className="font-serif-accent text-[10px] sm:text-xs tracking-wider text-[#D4AF37] uppercase mb-0.5 sm:mb-1">
                      Time
                    </p>
                    <p
                      className="font-serif-heading text-base sm:text-lg md:text-xl text-[#2D2D2D]"
                      data-testid="event-time"
                    >
                      {eventDetails.time}
                    </p>
                  </div>
                </motion.div>

                {/* Location */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="flex items-start gap-3 sm:gap-4 mb-5 sm:mb-8"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-[#D4AF37] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#8B0000]" />
                  </div>
                  <div>
                    <p className="font-serif-accent text-[10px] sm:text-xs tracking-wider text-[#D4AF37] uppercase mb-0.5 sm:mb-1">
                      Venue
                    </p>
                    <p
                      className="font-serif-heading text-base sm:text-lg md:text-xl text-[#2D2D2D] mb-0.5 sm:mb-1"
                      data-testid="venue-name"
                    >
                      {eventDetails.venue}
                    </p>
                    <p className="font-sans text-xs sm:text-sm text-[#5A5A5A]">
                      {eventDetails.address}
                    </p>
                    <p className="font-sans text-xs sm:text-sm text-[#5A5A5A]">
                      {eventDetails.city}
                    </p>
                  </div>
                </motion.div>

                {/* Map Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <Button
                    asChild
                    className="bg-[#8B0000] hover:bg-[#6B0000] text-white font-serif-accent text-[10px] sm:text-xs tracking-[0.1em] sm:tracking-[0.15em] uppercase py-4 sm:py-6 px-5 sm:px-8 transition-all duration-300 w-full sm:w-auto"
                    data-testid="view-map-btn"
                  >
                    <a
                      href={eventDetails.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                      View on Maps
                    </a>
                  </Button>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LocationCard;
